"use client"

import { XIcon } from "lucide-react"
import { cn } from "cn"
import { useState, type ReactNode } from "react"
import { Button } from "components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "components/ui/popover"

type ComboboxProps<T> = {
  ariaLabel: string
  className?: string
  contentClassName?: string
  disabled?: boolean
  emptyMessage?: ReactNode
  filterOptions?: (options: readonly T[], inputValue: string) => readonly T[]
  getOptionKey: (option: T) => string
  getOptionLabel: (option: T) => string
  id?: string
  inputValue: string
  isLoading?: boolean
  isOptionEqualToValue?: (option: T, value: T) => boolean
  loadingMessage?: ReactNode
  onClear?: () => void
  onInputValueChange: (inputValue: string) => void
  onSelectOption: (option: T) => void
  options: readonly T[]
  placeholder?: string
  renderOption?: (option: T) => ReactNode
  value?: T | null
}

export function Combobox<T>({
  ariaLabel,
  className,
  contentClassName,
  disabled = false,
  emptyMessage = "No options found.",
  filterOptions,
  getOptionKey,
  getOptionLabel,
  id,
  inputValue,
  isLoading = false,
  isOptionEqualToValue,
  loadingMessage = "Loading options...",
  onClear,
  onInputValueChange,
  onSelectOption,
  options,
  placeholder,
  renderOption,
  value,
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false)
  const filteredOptions = filterOptions
    ? filterOptions(options, inputValue)
    : options
  const isSelected = (option: T) =>
    value
      ? isOptionEqualToValue
        ? isOptionEqualToValue(option, value)
        : getOptionKey(option) === getOptionKey(value)
      : false

  return (
    <Command
      className="overflow-visible rounded-none bg-transparent"
      label={ariaLabel}
      shouldFilter={false}
    >
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverAnchor asChild>
          <div className={cn("relative", className)}>
            <CommandInput
              aria-autocomplete="list"
              aria-label={ariaLabel}
              disabled={disabled}
              id={id}
              onClick={() => setOpen(true)}
              onFocus={() => setOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setOpen(false)
                }
              }}
              onValueChange={(nextInputValue) => {
                setOpen(true)
                onInputValueChange(nextInputValue)
              }}
              placeholder={placeholder}
              value={inputValue}
              wrapperClassName="h-9 rounded-md border border-input bg-transparent px-3 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50"
            />
            {inputValue ? (
              <Button
                aria-label={`Clear ${ariaLabel}`}
                className="absolute top-1/2 right-1 -translate-y-1/2"
                disabled={disabled}
                onClick={() => {
                  if (onClear) {
                    onClear()
                  } else {
                    onInputValueChange("")
                  }

                  setOpen(false)
                }}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <XIcon aria-hidden="true" />
              </Button>
            ) : null}
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className={cn(
            "w-(--radix-popover-trigger-width) min-w-(--radix-popover-trigger-width) p-1",
            contentClassName,
          )}
          onOpenAutoFocus={(event) => event.preventDefault()}
          sideOffset={4}
        >
          <CommandList className="max-h-72 min-h-0">
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-muted-foreground" role="status">
                {loadingMessage}
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                {filteredOptions.map((option) => {
                  const selected = isSelected(option)

                  return (
                    <CommandItem
                      className={selected ? "bg-blue-50 text-blue-900" : undefined}
                      key={getOptionKey(option)}
                      onSelect={() => {
                        onSelectOption(option)
                        setOpen(false)
                      }}
                      value={getOptionKey(option)}
                    >
                      {renderOption ? renderOption(option) : getOptionLabel(option)}
                    </CommandItem>
                  )
                })}
              </>
            )}
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  )
}